/**
 * Card size is the one required accessibility setting, and it is first here for
 * that reason. Large is measured to fit (146 px hand band, ring ≥ 330 px) and
 * suits an older audience — it is worth offering on first run.
 *
 * Every control here is ordered with the shipped default first, so the row a
 * player lands on reads left to right as "this, or instead this".
 */
import { useState } from 'react'
import { Sheet } from '../table/PromptSheet'
import { prefersReducedMotion, useSettings, type CardScale } from './settings'
import { canBuzz } from './haptics'
import { requestTiltPermission } from '../table/tilt'

const SIZES: readonly { value: CardScale; label: string }[] = [
  { value: 's', label: 'Small' },
  { value: 'm', label: 'Medium' },
  { value: 'l', label: 'Large' },
]

export function SettingsSheet({
  onClose,
  onAccount,
  onLeave,
  midMatch = false,
}: {
  onClose: () => void
  onAccount?: () => void
  /** Present while at a table: the way out that is always one tap from the
   *  three-dot menu, not only on the lobby and the match-end screens. */
  onLeave?: () => void
  /** Leaving mid-match holds the seat rather than giving it up, and stops the
   *  table for everyone else, so it is asked twice. */
  midMatch?: boolean
}): JSX.Element {
  const [settings, set] = useSettings()
  const [confirmingLeave, setConfirmingLeave] = useState(false)
  const [tiltRefused, setTiltRefused] = useState(false)
  // iOS asks before a page may read the gyroscope, and only from a tap — this
  // one. Turned down, the setting stays off rather than on and doing nothing.
  const turnTiltOn = async () => {
    const granted = await requestTiltPermission()
    setTiltRefused(!granted)
    if (granted) set({ tilt: true })
  }

  return (
    <Sheet onDismiss={onClose}>
      <div className="sheet-head">
        <h2>Settings</h2>
      </div>

      <div className="sheet-body sheet-scroll">

        <div className="setting">
          <div className="setting-label">Card size</div>
          <div className="segmented">
            {SIZES.map((size) => (
              <button
                key={size.value}
                className={settings.cardScale === size.value ? 'on' : undefined}
                onClick={() => set({ cardScale: size.value })}
              >
                {size.label}
              </button>
            ))}
          </div>
        </div>

        <div className="setting">
          <div className="setting-label">Deck</div>
          <div className="segmented">
            <button
              className={settings.deck === 'twocolor' ? 'on' : undefined}
              onClick={() => set({ deck: 'twocolor' })}
            >
              Two colour
            </button>
            <button
              className={settings.deck === 'fourcolor' ? 'on' : undefined}
              onClick={() => set({ deck: 'fourcolor' })}
            >
              Four colour
            </button>
          </div>
        </div>

        <div className="setting">
          <div className="setting-label">Hand order</div>
          <div className="segmented">
            <button
              className={settings.handOrder === 'highRight' ? 'on' : undefined}
              onClick={() => set({ handOrder: 'highRight' })}
            >
              High right
            </button>
            <button
              className={settings.handOrder === 'highLeft' ? 'on' : undefined}
              onClick={() => set({ handOrder: 'highLeft' })}
            >
              High left
            </button>
          </div>
          <div className="setting-note">
            High right puts the trumps under your thumb, at the open end of the fan.
          </div>
        </div>

        <div className="setting">
          <div className="setting-label">Motion</div>
          <div className="segmented">
            <button
              className={settings.motion === 'system' ? 'on' : undefined}
              onClick={() => set({ motion: 'system' })}
            >
              System
            </button>
            <button
              className={settings.motion === 'reduce' ? 'on' : undefined}
              onClick={() => set({ motion: 'reduce' })}
            >
              Reduced
            </button>
          </div>
        </div>

        <div className="setting">
          <div className="setting-label">Coach</div>
          <div className="segmented">
            <button className={!settings.coach ? 'on' : undefined} onClick={() => set({ coach: false })}>
              Off
            </button>
            <button className={settings.coach ? 'on' : undefined} onClick={() => set({ coach: true })}>
              On
            </button>
          </div>
          <div className="setting-note">
            On your turn, the Hard bot marks the card or call it would choose, from only what you can see — at
            tables where the host has allowed coach mode.
          </div>
        </div>

        <div className="setting">
          <div className="setting-label">Tilt</div>
          <div className="segmented">
            <button className={settings.tilt ? undefined : 'on'} onClick={() => set({ tilt: false })}>
              Off
            </button>
            <button className={settings.tilt ? 'on' : undefined} onClick={() => void turnTiltOn()}>
              On
            </button>
          </div>
          <div className="setting-note">
            {tiltRefused
              ? 'Motion access was turned down. Allow it for this site in your browser’s settings, then try again.'
              : settings.tilt && prefersReducedMotion()
                ? 'The table holds still while motion is reduced.'
                : 'The cards catch the light and the table shifts a little as you move your phone.'}
          </div>
        </div>

        <div className="setting">
          <div className="setting-label">Flick to play</div>
          <div className="segmented">
            <button className={settings.flick ? 'on' : undefined} onClick={() => set({ flick: true })}>
              On
            </button>
            <button className={settings.flick ? undefined : 'on'} onClick={() => set({ flick: false })}>
              Off
            </button>
          </div>
          <div className="setting-note">
            Pick a card up and throw it at the table to play it. Tapping still works, and cards on the table can be nudged.
          </div>
        </div>

        <div className="setting">
          <div className="setting-label">Touch the felt</div>
          <div className="segmented">
            <button className={settings.feltTouch ? 'on' : undefined} onClick={() => set({ feltTouch: true })}>
              On
            </button>
            <button className={settings.feltTouch ? undefined : 'on'} onClick={() => set({ feltTouch: false })}>
              Off
            </button>
          </div>
          <div className="setting-note">
            Run a finger across the empty felt and the lamp&rsquo;s light follows it. The others see it too, in brass.
          </div>
        </div>

        <div className="setting">
          <div className="setting-label">Share my card moves</div>
          <div className="segmented">
            <button className={settings.shareMoves ? 'on' : undefined} onClick={() => set({ shareMoves: true })}>
              On
            </button>
            <button className={settings.shareMoves ? undefined : 'on'} onClick={() => set({ shareMoves: false })}>
              Off
            </button>
          </div>
          <div className="setting-note">
            The others see a card you pick up or nudge move, and that it is you holding it — never which card from your hand.
          </div>
        </div>

        <div className="setting">
          <div className="setting-label">Show the others’ card moves</div>
          <div className="segmented">
            <button className={settings.seeMoves ? 'on' : undefined} onClick={() => set({ seeMoves: true })}>
              On
            </button>
            <button className={settings.seeMoves ? undefined : 'on'} onClick={() => set({ seeMoves: false })}>
              Off
            </button>
          </div>
        </div>

        {canBuzz ? (
          <div className="setting">
            <div className="setting-label">Haptics</div>
            <div className="segmented">
              <button className={settings.haptics ? 'on' : undefined} onClick={() => set({ haptics: true })}>
                On
              </button>
              <button className={settings.haptics ? undefined : 'on'} onClick={() => set({ haptics: false })}>
                Off
              </button>
            </div>
          </div>
        ) : null}
      </div>

      {/* The ways out of the sheet stay in view however far the rows scroll,
          and so does the question leaving asks, which takes their place. */}
      <div className="sheet-footer">
        {onLeave && confirmingLeave ? (
          <div className="setting">
            <div className="setting-label">Leave the game?</div>
            <div className="setting-note">
              Your seat is held for you. Play goes on until your turn, then the table pauses until you come back or the others hand it to a bot.
            </div>
            <div className="option-row">
              <button className="btn btn-secondary" onClick={() => setConfirmingLeave(false)}>
                Stay
              </button>
              <button className="btn btn-primary" onClick={onLeave}>
                Leave
              </button>
            </div>
          </div>
        ) : (
          <div className="option-row">
            {onLeave ? (
              <button className="btn btn-secondary" onClick={() => (midMatch ? setConfirmingLeave(true) : onLeave())}>
                Leave game
              </button>
            ) : null}
            {onAccount ? (
              <button className="btn btn-secondary" onClick={onAccount}>
                Account
              </button>
            ) : null}
            <button className="btn btn-primary" onClick={onClose}>
              Done
            </button>
          </div>
        )}
      </div>
    </Sheet>
  )
}
